import React, { useState } from 'react';
import { AvailableTool } from '../../../lib/database';
import ToggleSwitch from './ToggleSwitch';
import NangoAuth from '../../../components/ui/NangoAuth';
import { supabase } from '../../../lib/supabase';

interface ToolInstance {
  id: string;
  toolId: string;
  name: string;
  description: string;
  tool_prompt: string;
  config: Record<string, any> | null;
  nango_connection_id?: string | null;
}

interface ToolManagementProps {
  selectedTools: ToolInstance[];
  onToolsChange: (tools: ToolInstance[]) => void;
  availableTools: AvailableTool[];
  agentId?: string | null;
}

type TestConnectionStatus = 'idle' | 'loading' | 'success' | 'error';

const ToolManagement: React.FC<ToolManagementProps> = ({ selectedTools, onToolsChange, availableTools, agentId }) => {
  console.log('[ToolManagement] Received Available Tools:', JSON.stringify(availableTools, null, 2));
  const [isAddToolModalOpen, setIsAddToolModalOpen] = useState(false);
  const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);
  const [isRemoveConfirmOpen, setIsRemoveConfirmOpen] = useState(false);
  const [toolToRemove, setToolToRemove] = useState<ToolInstance | null>(null);
  const [currentTool, setCurrentTool] = useState<ToolInstance | null>(null);
  const [toolConfig, setToolConfig] = useState<Record<string, any>>({});
  const [isDuplicateWarningOpen, setIsDuplicateWarningOpen] = useState(false);
  const [duplicateToolName, setDuplicateToolName] = useState<string>('');
  const [testConnectionStatus, setTestConnectionStatus] = useState<TestConnectionStatus>('idle');
  const [testConnectionMessage, setTestConnectionMessage] = useState<string>('');
  const [dbSchema, setDbSchema] = useState<Record<string, any> | null>(null);
  const [testApiRequestStatus, setTestApiRequestStatus] = useState<TestConnectionStatus>('idle');
  const [testApiRequestMessage, setTestApiRequestMessage] = useState<string>('');
  const [testApiResponse, setTestApiResponse] = useState<Record<string, any> | null>(null);
  
  const toolCategories = React.useMemo(() => {
    const backendCategories = Array.from(new Set(availableTools.map(tool => tool.category).filter((c): c is string => c !== null)));
    backendCategories.sort();
    return [...backendCategories, 'custom'];
  }, [availableTools]);

  const [activeCategory, setActiveCategory] = useState<string>(toolCategories[0] || 'custom');

  React.useEffect(() => {
    if (toolCategories.length > 0 && !toolCategories.includes(activeCategory)) {
      setActiveCategory(toolCategories[0]);
    }
  }, [toolCategories, activeCategory]);

  const generateToolInstanceId = () => {
    return 'tool_' + Date.now() + '_' + Math.random().toString(36).substring(2, 11);
  };

  const initializeConfig = (schema: Record<string, any>): Record<string, any> => {
    const config: Record<string, any> = {};
    for (const key in schema) {
      const value = schema[key];
      if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
        // New schema format: { "type": "...", "default": ... }
        if (value.hasOwnProperty('type')) {
          // Explicitly set the default for login_type if it exists
          if (key === 'login_type' && value.default) {
            config[key] = value.default;
          } else {
            config[key] = value.default;
          }
        } 
        // Old schema format or nested object
        else {
          config[key] = initializeConfig(value);
        }
      } else if (value === 'boolean') {
        config[key] = false;
      } else {
        config[key] = '';
      }
    }
    return config;
  };

  const selectTool = (tool: AvailableTool) => {
    if (selectedTools.some(t => t.toolId === tool.id)) {
      setDuplicateToolName(tool.name);
      setIsDuplicateWarningOpen(true);
      return;
    }

    const newConfig = tool.config ? initializeConfig(tool.config) : null;

    const toolInstance: ToolInstance = {
      id: generateToolInstanceId(),
      toolId: tool.id,
      name: tool.name,
      description: tool.description || '',
      tool_prompt: '',
      config: newConfig,
    };

    onToolsChange([...selectedTools, toolInstance]);
    setIsAddToolModalOpen(false);
  };

  const handleRemoveClick = (tool: ToolInstance) => {
    setToolToRemove(tool);
    setIsRemoveConfirmOpen(true);
  };

  const confirmRemoveTool = () => {
    if (toolToRemove) {
      onToolsChange(selectedTools.filter(t => t.id !== toolToRemove.id));
    }
    setIsRemoveConfirmOpen(false);
    setToolToRemove(null);
  };

  const updateToolUsage = (toolId: string, usage: string) => {
    onToolsChange(selectedTools.map(t => 
      t.id === toolId ? { ...t, tool_prompt: usage } : t
    ));
  };

  const openConfigModal = (tool: ToolInstance) => {
    setCurrentTool(tool);
    setToolConfig(tool.config || {});
    setTestConnectionStatus('idle');
    setTestConnectionMessage('');
    setDbSchema(null);
    setTestApiRequestStatus('idle');
    setTestApiRequestMessage('');
    setTestApiResponse(null);
    setIsConfigModalOpen(true);
  };

  const handleConfigChange = (path: string, value: any) => {
    setToolConfig(prev => {
      const newConfig = JSON.parse(JSON.stringify(prev));
      const keys = path.split('.');
      let current = newConfig;
      for (let i = 0; i < keys.length - 1; i++) {
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;
      return newConfig;
    });
  };

  const handleSaveConfiguration = () => {
    if (!currentTool) return;
    
    const connectionId = toolConfig.login_type === 'agent' ? `agent_${agentId}` : null;

    onToolsChange(
      selectedTools.map(t =>
        t.id === currentTool.id 
          ? { 
              ...t, 
              config: toolConfig,
              nango_connection_id: connectionId
            } 
          : t
      )
    );
    setIsConfigModalOpen(false);
    setCurrentTool(null);
    setToolConfig({});
  };

  const handleTestConnection = async () => {
    if (!currentTool) return;

    const dbConfig = toolConfig; // Config is at the top level
    if (!dbConfig || !dbConfig.db_driver) {
      setTestConnectionStatus('error');
      setTestConnectionMessage('Database configuration is incomplete.');
      return;
    }

    setTestConnectionStatus('loading');
    setTestConnectionMessage('');
    setDbSchema(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error("User not authenticated");
      }

      const response = await fetch('/v1/utils/db/test-connection', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          db_driver: dbConfig.db_driver,
          db_user: dbConfig.db_user,
          db_password: dbConfig.db_password,
          db_host: dbConfig.db_host,
          db_port: parseInt(dbConfig.db_port, 10),
          db_name: dbConfig.db_name,
          db_schema: dbConfig.db_schema || null,
        })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.detail || 'Failed to connect to the database.');
      }

      setTestConnectionStatus('success');
      setTestConnectionMessage(result.message || 'Connection successful.');
      setDbSchema(result.schema);

    } catch (error: any) {
      setTestConnectionStatus('error');
      setTestConnectionMessage(error.message || 'An unexpected error occurred.');
    }
  };

  const handleTestApiRequest = async () => {
    if (!currentTool) return;

    const apiConfig = toolConfig;
    if (!apiConfig || !apiConfig.url || !apiConfig.method) {
        setTestApiRequestStatus('error');
        setTestApiRequestMessage('API configuration is incomplete. URL and Method are required.');
        return;
    }

    setTestApiRequestStatus('loading');
    setTestApiRequestMessage('');
    setTestApiResponse(null);

    try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
            throw new Error("User not authenticated");
        }

        const parseJson = (jsonString: string) => {
            if (!jsonString) return null;
            try {
                return JSON.parse(jsonString);
            } catch (e) {
                return { raw_string: jsonString };
            }
        };

        const requestBody = {
            method: apiConfig.method,
            url: apiConfig.url,
            params: parseJson(apiConfig.params),
            headers: parseJson(apiConfig.headers),
            json_data: parseJson(apiConfig.json_data),
        };

        const backendUrl = process.env.REACT_APP_AI_BACKEND_URL;
        const response = await fetch(`${backendUrl}/v1/utils/api/test-request`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${session.access_token}`
            },
            body: JSON.stringify(requestBody)
        });

        const result = await response.json();

        if (!response.ok) {
            throw new Error(result.detail || 'Failed to execute API request.');
        }

        setTestApiRequestStatus('success');
        setTestApiRequestMessage(result.message || 'Request successful.');
        setTestApiResponse(result.response);

    } catch (error: any) {
        setTestApiRequestStatus('error');
        setTestApiRequestMessage(error.message || 'An unexpected error occurred.');
    }
  };

  const renderConfigFields = (schema: Record<string, any>, currentConfig: Record<string, any>, pathPrefix = '') => {
    const elements: React.ReactElement[] = [];
    const keys = Object.keys(schema);

    // Helper function to render a single field. This avoids code duplication.
    const renderField = (key: string) => {
      const currentPath = pathPrefix ? `${pathPrefix}.${key}` : key;
      const schemaValue = schema[key];
      const configValue = currentConfig ? currentConfig[key] : undefined;

      // Check for the new rich schema format first
      if (typeof schemaValue === 'object' && schemaValue !== null && !Array.isArray(schemaValue) && schemaValue.hasOwnProperty('type')) {
        const { type, options, default: defaultValue } = schemaValue;
        const label = key.replace(/_/g, ' ');
  
        if (type === 'subtitle') {
          // Note: Subtitles are handled separately before this function is called for other keys.
          // This check is here as a fallback.
          return (
            <div key={currentPath} className="pt-1">
              <p className="text-xs text-gray-500">{defaultValue}</p>
            </div>
          );
        }

        const commonLabel = <label className="block text-sm font-normal text-gray-700 mb-2 capitalize">{label}</label>;

        switch (type) {
          case 'boolean':
            return (
              <div key={currentPath} className="flex items-center justify-between py-1">
                <label className="text-sm font-normal text-gray-700 capitalize">{label}</label>
                <ToggleSwitch
                  id={currentPath}
                  checked={configValue ?? defaultValue ?? false}
                  onChange={(checked) => handleConfigChange(currentPath, checked)}
                />
              </div>
            );
          case 'select': {
            const isLoginType = key === 'login_type';
            if (!isLoginType) {
              // Default select rendering for non-login_type fields
              return (
                <div key={currentPath}>
                  {commonLabel}
                  <select
                    value={configValue ?? defaultValue ?? ''}
                    onChange={(e) => handleConfigChange(currentPath, e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-netlify-teal"
                  >
                    {options?.map((option: string) => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>
              );
            }

            // Enhanced rendering for the login_type field
            const toolDef = currentTool ? availableTools.find(t => t.id === currentTool.toolId) : null;
            const providerKey = toolDef?.oauth_provider;
            const isConnected = !!currentTool?.nango_connection_id;

            return (
              <div key={currentPath}>
                {commonLabel}
                <select
                  value={configValue ?? defaultValue ?? ''}
                  onChange={(e) => handleConfigChange(currentPath, e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-netlify-teal"
                >
                  {options?.map((option: string) => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
                
                {configValue === 'agent' && (
                  <div className="mt-4">
                    {!providerKey ? (
                      <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800">
                        OAuth (Agent) login is not configured for this tool.
                      </div>
                    ) : isConnected ? (
                      <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                        <div className="flex items-center space-x-2 text-green-800 font-medium">
                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                          <span>Connected</span>
                        </div>
                        <button 
                          onClick={() => {
                            // This will clear the connection on next save
                            handleConfigChange('login_type', 'none'); 
                          }}
                          className="text-sm text-gray-600 hover:text-red-600 font-medium"
                        >
                          Disconnect
                        </button>
                      </div>
                    ) : (
                      agentId && (
                        <NangoAuth
                          providerConfigKey={providerKey}
                          connectionId={`agent_${agentId}`}
                          onConnectionSuccess={() => {
                            // The handleSaveConfiguration will now set the nango_connection_id
                            // We just need to show the connected state visually
                            if(currentTool) {
                              setCurrentTool({...currentTool, nango_connection_id: `agent_${agentId}`});
                            }
                          }}
                          onConnectionError={(error) => console.error("Nango connection error:", error)}
                        />
                      )
                    )}
                  </div>
                )}
              </div>
            );
          }
          case 'private_key':
            return (
              <div key={currentPath}>
                {commonLabel}
                <textarea
                  rows={7}
                  value={configValue ?? defaultValue ?? ''}
                  onChange={(e) => handleConfigChange(currentPath, e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-netlify-teal font-mono text-xs"
                  placeholder={`Enter ${label}`}
                />
              </div>
            );
          default: { // Handles 'string', 'text', etc.
            const isJsonLike = ['params', 'headers', 'json_data'].includes(key);
            if (isJsonLike) {
              return (
                <div key={currentPath}>
                  {commonLabel}
                  <textarea
                    rows={3}
                    value={configValue ?? defaultValue ?? ''}
                    onChange={(e) => handleConfigChange(currentPath, e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-netlify-teal font-mono text-xs"
                    placeholder={`Enter ${label} as JSON`}
                  />
                </div>
              );
            }
            return (
              <div key={currentPath}>
                {commonLabel}
                <input
                  type="text"
                  value={configValue ?? defaultValue ?? ''}
                  onChange={(e) => handleConfigChange(currentPath, e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-netlify-teal"
                  placeholder={`Enter ${label}`}
                />
              </div>
            );
          }
        }
      } 
      // Handle nested objects (for grouping fields)
      else if (typeof schemaValue === 'object' && schemaValue !== null && !Array.isArray(schemaValue)) {
        const isTopLevelObject = pathPrefix === '';
        return (
          <div key={currentPath} className={isTopLevelObject ? "mt-4" : "border-l-2 border-gray-200 pl-4 pt-2 mt-2"}>
            <h4 className={`capitalize mb-2 ${isTopLevelObject ? 'text-base font-semibold text-gray-800' : 'text-sm font-semibold text-gray-700'}`}>{key.replace(/_/g, ' ')}</h4>
            <div className="space-y-3">
              {renderConfigFields(schemaValue, configValue || {}, currentPath)}
            </div>
          </div>
        );
      } 
      // Fallback for the old, simple schema format
      else {
        const isPrivateKey = key.includes('private_key');
        if (isPrivateKey) {
          return (
            <div key={currentPath}>
              <label className="block text-sm font-normal text-gray-700 mb-2 capitalize">{key.replace(/_/g, ' ')}</label>
              <textarea
                rows={7}
                value={configValue || ''}
                onChange={(e) => handleConfigChange(currentPath, e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-netlify-teal font-mono text-xs"
                placeholder={`Enter ${key.replace(/_/g, ' ')}`}
              />
            </div>
          );
        } else if (schemaValue === 'boolean') {
          return (
            <div key={currentPath} className="flex items-center justify-between py-1">
              <label className="text-sm font-normal text-gray-700 capitalize">{key.replace(/_/g, ' ')}</label>
              <ToggleSwitch
                id={currentPath}
                checked={configValue || false}
                onChange={(checked) => handleConfigChange(currentPath, checked)}
              />
            </div>
          );
        } else {
          return (
            <div key={currentPath}>
              <label className="block text-sm font-normal text-gray-700 mb-2 capitalize">{key.replace(/_/g, ' ')}</label>
              <input
                type="text"
                value={configValue || ''}
                onChange={(e) => handleConfigChange(currentPath, e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-netlify-teal"
                placeholder={`Enter ${key.replace(/_/g, ' ')}`}
              />
            </div>
          );
        }
      }
    };

    // First, render the 'descr' field if it exists and is a subtitle.
    if (keys.includes('descr')) {
      const descrSchema = schema['descr'];
      if (typeof descrSchema === 'object' && descrSchema !== null && descrSchema.type === 'subtitle') {
        const descrPath = pathPrefix ? `${pathPrefix}.descr` : 'descr';
        elements.push(
          <div key={descrPath} className="pt-1 mb-2">
            <p className="text-xs text-gray-500">{descrSchema.default}</p>
          </div>
        );
      }
    }

    // Then, render all other fields, skipping 'descr'.
    keys.forEach((key) => {
      if (key === 'descr' && schema[key]?.type === 'subtitle') {
        return;
      }
      elements.push(renderField(key));
    });
  
    return elements;
  };

  const renderToolConfig = (tool: ToolInstance | null) => {
    if (!tool) return null;

    const toolDef = availableTools.find(t => t.id === tool.toolId);

    if (!toolDef || !toolDef.config) {
      return (
        <div className="space-y-4">
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <svg className="w-5 h-5 text-gray-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h--1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
              <div>
                <p className="font-medium text-gray-800 text-sm">No Configuration Required</p>
                <p className="text-gray-600 text-sm mt-1">This tool uses default settings and is ready to use.</p>
              </div>
            </div>
          </div>
        </div>
      );
    }

    const configSchema = toolDef.config;
    const isDbTool = configSchema.hasOwnProperty('db_driver') && configSchema.hasOwnProperty('db_host');
    const isApiTool = configSchema.hasOwnProperty('url') && configSchema.hasOwnProperty('method');

    return (
      <div className="space-y-4">
        {renderConfigFields(configSchema, toolConfig)}

        {isDbTool && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <button
              onClick={handleTestConnection}
              disabled={testConnectionStatus === 'loading'}
              className="w-full px-4 py-2 text-sm font-medium text-white bg-netlify-teal rounded-lg hover:bg-netlify-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-netlify-teal disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {testConnectionStatus === 'loading' ? 'Testing...' : 'Test Connection'}
            </button>
            {testConnectionStatus !== 'idle' && (
              <div className="mt-3 p-3 rounded-lg text-sm">
                {testConnectionStatus === 'success' && (
                  <div className="bg-green-50 border border-green-200 text-green-800 p-3 rounded-lg">
                    <p className="font-semibold">{testConnectionMessage}</p>
                    {dbSchema && (
                      <div className="mt-2">
                        <p className="font-medium">Detected Schema:</p>
                        <pre className="text-xs bg-gray-800 text-white p-2 rounded-md mt-1 max-h-40 overflow-auto">
                          {JSON.stringify(dbSchema, null, 2)}
                        </pre>
                      </div>
                    )}
                  </div>
                )}
                {testConnectionStatus === 'error' && (
                  <div className="bg-red-50 border border-red-200 text-red-800 p-3 rounded-lg">
                    <p className="font-semibold">Error</p>
                    <p>{testConnectionMessage}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {isApiTool && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <button
              onClick={handleTestApiRequest}
              disabled={testApiRequestStatus === 'loading'}
              className="w-full px-4 py-2 text-sm font-medium text-white bg-netlify-teal rounded-lg hover:bg-netlify-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-netlify-teal disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {testApiRequestStatus === 'loading' ? 'Testing...' : 'Test API Request'}
            </button>
            {testApiRequestStatus !== 'idle' && (
              <div className="mt-3 p-3 rounded-lg text-sm">
                {testApiRequestStatus === 'success' && (
                  <div className="bg-green-50 border border-green-200 text-green-800 p-3 rounded-lg">
                    <p className="font-semibold">{testApiRequestMessage}</p>
                    {testApiResponse && (
                      <div className="mt-2">
                        <p className="font-medium">Response (Status: {testApiResponse.status_code}):</p>
                        <pre className="text-xs bg-gray-800 text-white p-2 rounded-md mt-1 max-h-40 overflow-auto">
                          {JSON.stringify(testApiResponse.body, null, 2)}
                        </pre>
                      </div>
                    )}
                  </div>
                )}
                {testApiRequestStatus === 'error' && (
                  <div className="bg-red-50 border border-red-200 text-red-800 p-3 rounded-lg">
                    <p className="font-semibold">Error</p>
                    <p>{testApiRequestMessage}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const getToolsByCategory = (category: string) => {
    return availableTools.filter(tool => tool.category === category);
  };

  return (
    <div className="bg-white rounded-xl p-6 space-y-6">
      {/* Tools Header */}
      <div className="flex items-center justify-between">
        <div>
          <h4 className="text-lg font-semibold text-gray-900">Agent Tools</h4>
          <p className="text-sm text-gray-600 mt-1">Tools allow your agent to perform actions beyond conversation</p>
        </div>
        <button 
          onClick={() => setIsAddToolModalOpen(true)}
          className="flex items-center space-x-2 px-4 py-2 bg-netlify-teal text-white hover:bg-netlify-600 rounded-lg transition-colors font-medium"
        >
          <svg className="w-5 h-5 hidden sm:inline-block" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"/>
          </svg>
          <span>Add Tool</span>
        </button>
      </div>

      {/* Selected Tools List */}
      <div className="space-y-4">
        {selectedTools.length === 0 ? (
          <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
            <svg className="w-12 h-12 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.781 0-2.678-2.153-1.415-3.414l5-5A2 2 0 009 9.172V5L8 4z"/>
            </svg>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No tools added yet</h3>
            <p className="text-sm sm:text-base text-gray-600 mb-4">Add tools to give your agent additional capabilities</p>
            <button 
              onClick={() => setIsAddToolModalOpen(true)}
              className="inline-flex items-center px-4 py-2 bg-netlify-teal text-white hover:bg-netlify-600 rounded-lg transition-colors font-medium"
            >
              <svg className="w-5 h-5 mr-2 hidden sm:inline-block" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"/>
              </svg>
              Add Your First Tool
            </button>
          </div>
        ) : (
          selectedTools.map((tool) => {
            return (
              <div key={tool.id} className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="grid grid-cols-[1fr_auto] items-start gap-x-4">
                  <div className="flex items-start space-x-3">
                    <div className="w-10 h-10 bg-gray-100 rounded-lg flex-shrink-0 items-center justify-center hidden sm:flex">
                      {/* Generic Icon */}
                      <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.781 0-2.678-2.153-1.415-3.414l5-5A2 2 0 009 9.172V5L8 4z" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <h5 className="font-medium text-gray-900">{tool.name}</h5>
                      <p className="text-sm text-gray-600 mt-1 hidden sm:block">{tool.description}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    {tool.config && Object.keys(tool.config).length > 0 && (
                      <button 
                        onClick={() => openConfigModal(tool)}
                        className="text-sm text-netlify-teal hover:text-netlify-600 font-medium"
                      >
                        Configure
                      </button>
                    )}
                    <button 
                      onClick={() => handleRemoveClick(tool)}
                      className="text-gray-400 hover:text-red-600 p-1 rounded-md transition-colors"
                      aria-label="Remove tool"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                      </svg>
                    </button>
                  </div>
                </div>
                <p className="text-sm text-gray-600 mt-2 sm:hidden">{tool.description}</p>
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">How should the agent use this tool?</label>
                  <textarea 
                    placeholder="e.g., Use this tool when users ask about..."
                    value={tool.tool_prompt}
                    onChange={(e) => updateToolUsage(tool.id, e.target.value)}
                    className="w-full text-sm p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-netlify-teal focus:border-transparent resize-none" 
                    rows={3}
                  />
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Add Tool Modal */}
      {isAddToolModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Add Tool</h3>
                  <p className="text-sm text-gray-600 mt-1">Select a tool to add to your agent</p>
                </div>
                <button 
                  onClick={() => setIsAddToolModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
                  </svg>
                </button>
              </div>
            </div>
            
            {/* Tool Categories */}
            <div className="border-b border-gray-200">
              <nav className="flex space-x-8 px-6">
                {toolCategories.map((category) => (
                  <button
                    key={category}
                    onClick={() => setActiveCategory(category)}
                    className={`py-4 px-1 border-b-2 font-medium text-sm capitalize ${
                      activeCategory === category 
                        ? 'border-netlify-teal text-netlify-teal' 
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    {category.replace('_', ' ')}
                  </button>
                ))}
              </nav>
            </div>

            {/* Tool Selection Grid */}
            <div className="p-6">
              {activeCategory === 'custom' ? (
                <div className="text-center py-8">
                  <svg className="w-12 h-12 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M12 6v6m0 0v6m0-6h6m-6 0H6"/>
                  </svg>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Custom Tools</h3>
                  <p className="text-gray-600 mb-4">Create your own tools using custom code</p>
                  <button className="inline-flex items-center px-4 py-2 bg-netlify-teal text-white hover:bg-netlify-600 rounded-lg transition-colors font-medium">
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"/>
                    </svg>
                    Create Custom Tool
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {getToolsByCategory(activeCategory).map((tool) => (
                    <div
                      key={tool.id}
                      onClick={() => selectTool(tool)}
                      className="p-4 bg-white border border-gray-200 rounded-lg hover:border-netlify-teal cursor-pointer transition-all"
                    >
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                          {/* Generic Icon */}
                          <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.781 0-2.678-2.153-1.415-3.414l5-5A2 2 0 009 9.172V5L8 4z" />
                          </svg>
                        </div>
                        <div>
                          <h4 className="font-medium text-gray-900">{tool.name}</h4>
                          <p className="text-sm text-gray-600">{tool.description}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Tool Configuration Modal */}
      {isConfigModalOpen && currentTool && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Configure {currentTool.name}</h3>
                <button 
                  onClick={() => setIsConfigModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
                  </svg>
                </button>
              </div>
            </div>
            
            <div className="p-6">
              {renderToolConfig(currentTool)}
              <div className="flex justify-end space-x-3 pt-4">
                <button 
                  onClick={() => setIsConfigModalOpen(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg"
                >
                  Cancel
                </button>
                <button 
                  onClick={handleSaveConfiguration}
                  className="px-4 py-2 bg-netlify-teal text-white hover:bg-netlify-600 rounded-lg"
                >
                  Save Configuration
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Remove Tool Confirmation Modal */}
      {isRemoveConfirmOpen && toolToRemove && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-md w-full">
            <div className="p-6">
              <div className="text-center">
                <svg className="w-12 h-12 mx-auto text-red-500 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
                </svg>
                <h3 className="text-lg font-semibold text-gray-900">Remove Tool</h3>
                <p className="text-sm text-gray-600 mt-2">
                  Are you sure you want to remove the tool "{toolToRemove.name}"? This action cannot be undone.
                </p>
              </div>
            </div>
            <div className="bg-gray-50 px-6 py-4 flex justify-end space-x-3 rounded-b-xl">
              <button 
                onClick={() => setIsRemoveConfirmOpen(false)}
                className="px-4 py-2 text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-lg font-medium text-sm"
              >
                Cancel
              </button>
              <button 
                onClick={confirmRemoveTool}
                className="px-4 py-2 bg-red-600 text-white hover:bg-red-700 rounded-lg font-medium text-sm"
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Duplicate Tool Warning Modal */}
      {isDuplicateWarningOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-md w-full">
            <div className="p-6">
              <div className="text-center">
                <svg className="w-12 h-12 mx-auto text-yellow-500 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
                </svg>
                <h3 className="text-lg font-semibold text-gray-900">Duplicate Tool</h3>
                <p className="text-sm text-gray-600 mt-2">
                  The tool "{duplicateToolName}" has already been added.
                </p>
              </div>
            </div>
            <div className="bg-gray-50 px-6 py-4 flex justify-end space-x-3 rounded-b-xl">
              <button 
                onClick={() => setIsDuplicateWarningOpen(false)}
                className="px-4 py-2 text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-lg font-medium text-sm"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ToolManagement;